import { Dialog, DialogContent } from "@bleu.builders/ui";
import { useUser } from "@contexts/UserContext";
import { type Action, hasPermission } from "@lib/permissions";
import { LockClosedIcon } from "@radix-ui/react-icons";
// biome-ignore lint/style/useImportType: <explanation>
import React from "react";
import { useParams } from "react-router-dom";

function NotAllowed() {
  const params = useParams();

  return (
    <Dialog aria-labelledby="not-permitted" open>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        closeButton={<></>}
        aria-label="not-permitted"
        className="sm:max-w-[550px] p-6 flex flex-col gap-4 bg-background dark:border-2 rounded-lg shadow-xl"
      >
        <div className="flex items-center gap-4">
          <LockClosedIcon className="size-6 text-foreground animate-spin-slow" />
          <h2 className="text-lg font-semibold text-foreground">
            You are not allowed to access this page
          </h2>
        </div>
        <p className="text-foreground">
          You do not have permission to access this page. Please contact your
          administrator.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export const withAccessControl = (
  Component: React.ComponentType<any>,
  predicate: (permissions: any) => boolean,
) => {
  return (props: any) => {
    const { permissions } = useUser();

    const hasPermission = predicate(permissions);

    if (!hasPermission) {
      return <NotAllowed />;
    }

    return <Component {...props} />;
  };
};

export const withPermission = (
  Component: React.ComponentType<any>,
  resource: string,
  action: Action,
) => {
  return (props: any) => {
    const { permissions } = useUser();

    const userHasPermission = hasPermission(permissions, resource, action);

    if (!userHasPermission) {
      return <NotAllowed />;
    }

    return <Component {...props} />;
  };
};

export const PermissionRequiredContent = ({ resource, action, children }) => {
  const { permissions } = useUser();
  const userHasPermission = hasPermission(permissions, resource, action);

  return userHasPermission ? children : null;
};
